import type {
  AddToteResponse,
  AddTrolleyResponse,
  AssetTrackingData,
  CheckToteResponse,
  GetNotificationsResponse,
  LocationListResponse,
  MarkNotificationsRequest,
  MarkNotificationsResponse,
  MarkTotePriorityResponse,
  MarkTrolleyServiceableResponse,
  PrimaryLocationItem,
  PrimaryLocationListResponse,
  Tote,
  ToteListItem,
  TotePriorityListResponse,
  TotePriorityOption,
  Trolley,
  TrolleyListItem,
} from "./types";
import axiosInstance from "../config/axiosConfig";
import URL from "../config/envURLs";
import { convertLocalToUTC, convertUTCToLocal } from "../utils/timeUtils";

const BASE_URL = URL.API.baseURL.endsWith("/")
  ? URL.API.baseURL
  : `${URL.API.baseURL}/`;

const parseNumericId = (value: unknown): number | undefined => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const convertToteIdToNumber = (value: string | number): number => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === "string") {
    // Try to parse as number first
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    // Extract numeric digits from string (e.g., "TOTE123" -> 123)
    const numericPart = value.replace(/\D/g, "");
    if (numericPart) {
      const num = Number(numericPart);
      if (!Number.isNaN(num)) {
        return num;
      }
    }
  }
  return 0;
};

const normalizeLocationType = (value: unknown): "In" | "Out" | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "in") {
      return "In";
    }
    if (normalized === "out") {
      return "Out";
    }
  }

  return null;
};

const normalizeToteStatusLabel = (status: unknown): string | undefined => {
  if (typeof status !== "string") {
    return undefined;
  }

  const trimmed = status.trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.toLowerCase() === "available to pack") {
    return "Available";
  }

  return trimmed;
};

const mapToteListItemToTote = (item: ToteListItem): Tote => ({
  id: item.id.toString(),
  toteNo: item.tote_no,
  weight: item.weight ? `${item.weight}kg` : "0kg",
  itemCount: item.total_items?.toString() || "0",
  msn: item.msn || "",
  usn: item.usn || item.usm || "",
  usm: item.usm || "",
  priority: item.priority_flag === "High",
  lastScannedLocation: item.location
    ? `${item.location}${item.sublocation ? ` • ${item.sublocation}` : ""}`
    : "",
  activityStatus: normalizeToteStatusLabel(item.status) || "Available",
  isLoadedToTrolley: item.status === "Loaded to Trolley",
  registeredDate: item.created_at ? item.created_at : undefined,
  locationBarcode: item.location_barcode || undefined,
  recordId: parseNumericId(item.id),
});

const mapTrolleyListItemToTrolley = (item: TrolleyListItem): Trolley => {
  const primaryLocation = item.location || null;
  const locationType = normalizeLocationType(item.current_location_type);
  const parts: string[] = [];

  if (primaryLocation) {
    const formattedPrimary = locationType
      ? `${primaryLocation} (${locationType})`
      : primaryLocation;
    parts.push(formattedPrimary);
  }

  if (item.sublocation) {
    parts.push(item.sublocation);
  }

  return {
    id: item.trolley_id || item.id?.toString() || "",
    status: item.status || "Empty - Available",
    lastScannedLocation: parts.length ? parts.join(" • ") : "",
    priority: Boolean(item.priority_flag),
    toteId: null,
    toteIds: [],
    registeredDate: item.created_at || undefined,
    availableTotes: item.available_totes || 0,
    locationBarcode: item.location_barcode || undefined,
    currentLocationType: locationType,
    primaryLocation,
    secondaryLocation: item.sublocation || null,
    lastScannedDateTime: item.updated_at || null,
    recordId: parseNumericId(item.id) ?? parseNumericId(item.trolley_id),
  };
};

const buildQueryString = (
  builder: (params: URLSearchParams) => void
): string => {
  const params = new URLSearchParams();
  builder(params);
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const api = {
  getToteById: async (id: string): Promise<Tote> => {
    const numericId = convertToteIdToNumber(id);
    const response = await axiosInstance.get(
      `${BASE_URL}toteManagement/getToteInfo/${numericId}`
    );
    const data = response.data;
    const toteInfo = data.tote_info || data.tote_details;

    if (!toteInfo) {
      throw new Error("Tote not found");
    }

    // The numericId we used in the URL is the record ID, so use it as fallback
    const numericRecordId = typeof numericId === 'number' ? numericId : Number(numericId);

    const buildLocationDetails = (
      source:
        | {
            primary_location?: string | null;
            secondary_location?: string | null;
            scanned_date_time?: string | null;
            location_status?: string | null;
            location_Status?: string | null;
            status?: string | null;
            current_location_type?: string | null;
          }
        | undefined,
      fallbackType?: unknown
    ) => {
      if (!source) {
        const normalizedFallback = normalizeLocationType(fallbackType);
        return {
          status: normalizedFallback,
          location: "",
          dateTime: null as string | null,
        };
      }

      const normalizedStatus =
        normalizeLocationType(source.current_location_type) ??
        normalizeLocationType(source.location_status) ??
        normalizeLocationType(source.location_Status) ??
        normalizeLocationType(source.status) ??
        normalizeLocationType(fallbackType) ??
        null;

      const parts: string[] = [];

      if (source.primary_location) {
        const formattedPrimary = normalizedStatus
          ? `${source.primary_location} (${normalizedStatus})`
          : source.primary_location;
        parts.push(formattedPrimary);
      }

      if (source.secondary_location) {
        parts.push(source.secondary_location);
      }

      if (source.scanned_date_time) {
        parts.push(convertUTCToLocal(source.scanned_date_time));
      }

      return {
        status: normalizedStatus,
        location: parts.join(" • "),
        dateTime: source.scanned_date_time ?? null,
      };
    };

    const toteLocation = buildLocationDetails(
      toteInfo.last_scanned_location,
      (toteInfo as { current_location_type?: unknown }).current_location_type
    );

    const trolleyInfo = data.trolley_info;
    const isLoadedToTrolley =
      trolleyInfo?.trolley_id &&
      trolleyInfo.trolley_id !== "Tote Not loaded into trolley" &&
      trolleyInfo.trolley_id !== null;

    const trolleyLocation = buildLocationDetails(
      trolleyInfo?.last_scanned_location,
      trolleyInfo?.current_location_type
    );

    // Extract record ID from response, with fallback to the numericId used in the API call
    const recordId =
      parseNumericId(toteInfo.id) ??
      parseNumericId(toteInfo.tote_id) ??
      parseNumericId(data?.tote_details?.id) ??
      (isNaN(numericRecordId) ? undefined : numericRecordId);

    const registeredDate =
      toteInfo.registered_on ||
      toteInfo.registered_date ||
      toteInfo.created_at ||
      undefined;

    const normalizedActivityStatus =
      normalizeToteStatusLabel(toteInfo.status) || "Available";

    return {
      id,
      toteNo: toteInfo.tote_no || toteInfo.tote_id || id,
      weight: toteInfo.weight ? `${toteInfo.weight}kg` : "0kg",
      itemCount: (toteInfo.item_count || toteInfo.total_items || 0).toString(),
      msn: toteInfo.msn || "",
      usn: toteInfo.usn || toteInfo.usm || "",
      usm: toteInfo.usm || "",
      priority:
        toteInfo.priority === "High" ||
        toteInfo.priority === true ||
        toteInfo.priority_flag === "High" ||
        false,
      lastScannedLocation: toteLocation.location,
      activityStatus: normalizedActivityStatus,
      isLoadedToTrolley:
        isLoadedToTrolley || normalizedActivityStatus === "Loaded to Trolley",
      trolleyId: isLoadedToTrolley ? trolleyInfo.trolley_id : undefined,
      trolleyStatus: isLoadedToTrolley
        ? trolleyInfo?.status || "N/A"
        : undefined,
      trolleyLastScannedLocation:
        isLoadedToTrolley && trolleyLocation.location
          ? trolleyLocation.location
          : undefined,
      trolleyLastScannedDateTime: trolleyLocation.dateTime,
      locationBarcode: toteInfo.location_barcode || undefined,
      currentLocationType: toteLocation.status ?? undefined,
      recordId,
      registeredDate,
      pointOfDelivery: toteInfo.point_of_delivery || undefined,
      dateOfDelivery: toteInfo.date_of_delivery || undefined,
      pointOfUse: toteInfo.point_of_use_station || toteInfo.point_of_use || undefined,
      dateOfUse: toteInfo.date_of_use || undefined,
    };
  },

  getAllTotes: async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status_ids?: number[];
    priority_ids?: number[];
    location_id?: number;
    location_barcode?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<Tote[]> => {
    const queryString = buildQueryString((queryParams) => {
      const options = params || { page: 1, page_size: 100 };
      if (options.page) queryParams.append("page", options.page.toString());
      if (options.page_size)
        queryParams.append("page_size", options.page_size.toString());
      if (options.search) queryParams.append("search", options.search);
      if (options.status_ids?.length) {
        queryParams.append("status_ids", options.status_ids.join(","));
      }
      if (options.priority_ids?.length) {
        queryParams.append("priority_ids", options.priority_ids.join(","));
      }
      if (options.location_id)
        queryParams.append("location_id", options.location_id.toString());
      if (options.location_barcode)
        queryParams.append("location_barcode", options.location_barcode);
      if (options.from_date)
        queryParams.append("from_date", convertLocalToUTC(options.from_date));
      if (options.to_date)
        queryParams.append("to_date", convertLocalToUTC(options.to_date));
    });

    const response = await axiosInstance.get(
      `${BASE_URL}toteManagement/getToteList${queryString}`
    );
    const data = response.data;

    if (data?.totes) {
      return data.totes.map(mapToteListItemToTote);
    }

    return [];
  },

  getTrolleyById: async (id: string): Promise<Trolley> => {
    if (!id || String(id).trim() === "") {
      throw new Error("Trolley ID is required");
    }

    // Ensure ID is always treated as a string (not converted to number)
    const stringId = String(id).trim();
    const encodedId = encodeURIComponent(stringId);
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}trolleyManagement/getTrolleyInfo?trolley_id=${encodedId}`
      );
      const data = response.data;

      // Check for error messages in response (even with status_code 200)
      // Handle cases where API returns 200 with error message indicating trolley isn't registered
      const message = data?.message || "";
      const hasErrorMessage = message.includes("isn't registered") || 
                             message.includes("not found") || 
                             message.includes("doesn't exist");
      
      if (hasErrorMessage) {
        throw new Error(message || "Trolley not found");
      }

      if (!data?.trolley_info) {
        throw new Error("Trolley not found");
      }

    const trolleyInfo = data.trolley_info;
    const toteInfo = data.tote_info || {};

    let registeredDate: string | undefined;
    const registeredDateValue =
      trolleyInfo.registered_date ||
      trolleyInfo.registered_on ||
      trolleyInfo.created_at;
    if (registeredDateValue) {
      registeredDate = registeredDateValue;
    }

    let lastScannedLocation = "N/A";
    let primaryLocation: string | null = null;
    let secondaryLocation: string | null = null;
    let scannedDateTime: string | null = null;
    let lastLocationStatus: "In" | "Out" | null = null;
    const normalizedCurrentTypeFromPayload = normalizeLocationType(
      trolleyInfo.current_location_type
    );
    if (trolleyInfo.last_scanned_location) {
      const loc = trolleyInfo.last_scanned_location;
      const parts: string[] = [];

      const locationStatus =
        normalizedCurrentTypeFromPayload ??
        normalizeLocationType(loc.location_status) ??
        normalizeLocationType(loc.location_Status) ??
        normalizeLocationType(loc.status) ??
        null;

      lastLocationStatus = locationStatus;

      if (loc.primary_location) {
        primaryLocation = loc.primary_location;
        const formattedPrimary = locationStatus
          ? `${loc.primary_location} (${locationStatus})`
          : loc.primary_location;
        parts.push(formattedPrimary);
      }
      if (loc.secondary_location) {
        secondaryLocation = loc.secondary_location;
        parts.push(loc.secondary_location);
      }
      if (loc.scanned_date_time) {
        parts.push(convertUTCToLocal(loc.scanned_date_time));
      }

      if (parts.length > 0) {
        console.log(parts, "here part");

        lastScannedLocation = parts.join(" • ");
      }
    }

    const effectiveCurrentLocationType =
      normalizedCurrentTypeFromPayload ?? lastLocationStatus ?? null;

    // Handle new format with totes array (includes priority)
    let toteIds: string[] = [];
    let totes: Array<{ tote_id: string; priority?: string | null }> | undefined = undefined;
    
    if (toteInfo.totes && Array.isArray(toteInfo.totes) && toteInfo.totes.length > 0) {
      // New format: totes array with tote_id and priority
      totes = toteInfo.totes;
      toteIds = toteInfo.totes.map(tote => tote.tote_id);
    } else if (toteInfo.tote_numbers && Array.isArray(toteInfo.tote_numbers)) {
      // Legacy format: tote_numbers array
      toteIds = toteInfo.tote_numbers;
    }
    
    const toteId = toteIds.length > 0 ? toteIds[0] : null;
    const trolleyRecordId =
      parseNumericId(trolleyInfo.id) ?? parseNumericId(trolleyInfo.trolley_id);

    return {
      id: trolleyInfo.trolley_id || id,
      status: trolleyInfo.status || "Empty - Available",
      lastScannedLocation,
      priority:
        trolleyInfo.priority_flag === true ||
        trolleyInfo.priority_flag === "true",
      toteId,
      toteIds,
      totes,
      registeredDate,
      availableTotes: toteInfo.total_totes || 0,
      locationBarcode: trolleyInfo.location_barcode || undefined,
      currentLocationType:
        trolleyInfo.current_location_type ||
        (lastLocationStatus as "In" | "Out" | null) ||
        null,
      primaryLocation,
      secondaryLocation,
      lastScannedDateTime: scannedDateTime,
      mdu: trolleyInfo.mdu || undefined,
      recordId: trolleyRecordId,
    };
    } catch (error: any) {
      // Re-throw axios errors or custom errors
      if (error instanceof Error) {
        throw error;
      }
      // Handle axios error responses
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Trolley not found");
    }
  },

  getAllTrolleys: async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status_ids?: number[];
    priority_flag?: boolean | boolean[];
    location_id?: number;
    location_barcode?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<Trolley[]> => {
    const queryString = buildQueryString((queryParams) => {
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.page_size)
        queryParams.append("page_size", params.page_size.toString());
      if (params?.search) queryParams.append("search", params.search);
      if (params?.status_ids?.length) {
        queryParams.append("status_ids", params.status_ids.join(","));
      }
      if (params?.priority_flag !== undefined) {
        if (Array.isArray(params.priority_flag)) {
          params.priority_flag.forEach((flag) =>
            queryParams.append("priority_flag", flag.toString())
          );
        } else {
          queryParams.append("priority_flag", params.priority_flag.toString());
        }
      }
      if (params?.location_id)
        queryParams.append("location_id", params.location_id.toString());
      if (params?.location_barcode)
        queryParams.append("location_barcode", params.location_barcode);
      if (params?.from_date)
        queryParams.append("from_date", convertLocalToUTC(params.from_date));
      if (params?.to_date)
        queryParams.append("to_date", convertLocalToUTC(params.to_date));
    });

    const response = await axiosInstance.get(
      `${BASE_URL}trolleyManagement/getTrolleyList${queryString}`
    );
    const data = response.data;

    if (data?.trolleys) {
      return data.trolleys.map(mapTrolleyListItemToTrolley);
    }

    return [];
  },

  loadToteToTrolley: async (toteId: string | number, trolleyId: string) => {
    // Ensure we always send a numeric ID
    let numericToteId: number;
    if (typeof toteId === 'number') {
      numericToteId = toteId;
    } else {
      const parsed = Number(toteId);
      // If parsing fails (NaN), throw an error instead of sending the string
      if (isNaN(parsed)) {
        throw new Error(`Invalid tote ID: ${toteId}. Expected a numeric record ID.`);
      }
      numericToteId = parsed;
    }
    
    const response = await axiosInstance.post(
      `${BASE_URL}trolleyManagement/loadToteToTrolley`,
      {
        tote_id: numericToteId,
        trolley_id: trolleyId,
      }
    );
    return response.data;
  },

  unloadToteFromTrolley: async (toteId: string | number, trolleyId?: string) => {
    const response = await axiosInstance.post(
      `${BASE_URL}trolleyManagement/unloadToteFromTrolley`,
      {
        tote_id: typeof toteId === 'number' ? toteId : Number(toteId) || toteId,
        ...(trolleyId ? { trolley_id: trolleyId } : {}),
      }
    );
    return response.data;
  },

  updateToteLocation: async (
    toteId: string | number,
    locationBarcode: string,
    locationType: "In" | "Out"
  ) => {
    const response = await axiosInstance.post(
      `${BASE_URL}toteManagement/updateToteLocation`,
      {
        id: typeof toteId === 'number' ? toteId : Number(toteId) || toteId,
        location_barcode: locationBarcode,
        location_type: locationType,
      }
    );
    return response.data;
  },

  updateTrolleyLocation: async (
    trolleyId: string,
    locationBarcode: string,
    locationType: "In" | "Out"
  ) => {
    const response = await axiosInstance.post(
      `${BASE_URL}trolleyManagement/updateTrolleyLocation`,
      {
        trolley_id: trolleyId,
        location_barcode: locationBarcode,
        location_type: locationType,
      }
    );
    return response.data;
  },

  getAllAssetTracking: async (
    filter?: string
  ): Promise<AssetTrackingData[]> => {
    const response = await axiosInstance.get(
      `${BASE_URL}assetTracking/getAssetTracking`,
      {
        params: filter ? { filter } : undefined,
      }
    );

    const data = response.data;
    if (Array.isArray(data)) {
      return data as AssetTrackingData[];
    }
    if (Array.isArray(data?.data)) {
      return data.data as AssetTrackingData[];
    }
    return [];
  },

  addTote: async (payload: {
    tote_id: string;
    msn: string;
    usn: string;
    item_count: number;
    weight: number;
    status?: number;
    point_of_delivery?: string;
    date_of_delivery?: string;
    point_of_use_station?: string;
    date_of_use?: string;
  }): Promise<AddToteResponse> => {
    const apiUrl = `${BASE_URL}toteManagement/addTote`;
    const requestPayload: any = {
      tote_id: String(payload.tote_id).trim(),
      msn: String(payload.msn).trim(),
      usn: String(payload.usn).trim(),
      item_count: Number(payload.item_count),
      weight: Number(payload.weight),
      status: payload.status !== undefined ? Number(payload.status) : 1,
    };

    // Add new fields if provided
    if (payload.point_of_delivery) {
      requestPayload.point_of_delivery = String(payload.point_of_delivery).trim();
    }
    if (payload.date_of_delivery) {
      requestPayload.date_of_delivery = String(payload.date_of_delivery).trim();
    }
    if (payload.point_of_use_station) {
      requestPayload.point_of_use_station = String(payload.point_of_use_station).trim();
    }
    if (payload.date_of_use) {
      requestPayload.date_of_use = String(payload.date_of_use).trim();
    }

    const response = await axiosInstance.post<AddToteResponse>(apiUrl, requestPayload);
    return response.data;
  },

  getToteStatus: async () => {
    const response = await axiosInstance.get(
      `${BASE_URL}toteManagement/getToteStatus`
    );
    return response.data;
  },

  getLocations: async (): Promise<LocationListResponse> => {
    const response = await axiosInstance.get(
      `${BASE_URL}toteManagement/getLocations`
    );
    return response.data;
  },

  getPrimaryLocations: async (): Promise<PrimaryLocationItem[]> => {
    const response = await axiosInstance.get<PrimaryLocationListResponse>(
      `${BASE_URL}toteManagement/getPrimaryLocations`
    );
    const data = response.data;
    if (Array.isArray(data?.primary_locations)) {
      return data.primary_locations.map((item) => ({
        id: item.id,
        location_name: item.location_name,
        location_code: item.location_code,
      }));
    }
    return [];
  },

  getTrolleyStatus: async () => {
    const response = await axiosInstance.get(
      `${BASE_URL}trolleyManagement/getTrolleyStatus`
    );
    return response.data;
  },

  checkTrolley: async (payload: { trolley_id: string }) => {
    // Ensure trolley_id is always a string and not converted to number
    const stringTrolleyId = String(payload.trolley_id).trim();
    const requestPayload = {
      trolley_id: stringTrolleyId
    };
    
    const response = await axiosInstance.post(
      `${BASE_URL}trolleyManagement/checkTrolley`,
      requestPayload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  },

  addTrolley: async (payload: { 
    trolley_id: string;
    mdu: string; // MDU is now required
    status_id?: number;
  }): Promise<AddTrolleyResponse> => {
    const requestPayload: any = {
      trolley_id: payload.trolley_id,
      mdu: payload.mdu, // MDU is required
    };
    
    // Add optional status_id if provided
    if (payload.status_id !== undefined) {
      requestPayload.status_id = payload.status_id;
    }
    
    const response = await axiosInstance.post<AddTrolleyResponse>(
      `${BASE_URL}trolleyManagement/addTrolley`,
      requestPayload
    );
    return response.data;
  },

  checkTote: async (payload: {
    tote_id: string;
  }): Promise<CheckToteResponse> => {
    const response = await axiosInstance.post(
      `${BASE_URL}toteManagement/checkTote`,
      {
        tote_id: String(payload.tote_id).trim(),
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  },

  getToteInfo: async (toteId: string | number) => {
    const numericId = convertToteIdToNumber(toteId);
    const response = await axiosInstance.get(
      `${BASE_URL}toteManagement/getToteInfo/${numericId}`
    );
    return response.data;
  },

  getToteList: async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status_ids?: number[];
    priority_ids?: number[];
    from_date?: string;
    to_date?: string;
  }) => {
    const queryString = buildQueryString((queryParams) => {
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.page_size)
        queryParams.append("page_size", params.page_size.toString());
      if (params?.search) queryParams.append("search", params.search);
      if (params?.status_ids) {
        params.status_ids.forEach((id) =>
          queryParams.append("status_ids", id.toString())
        );
      }
      if (params?.priority_ids) {
        params.priority_ids.forEach((id) =>
          queryParams.append("priority_ids", id.toString())
        );
      }
      if (params?.from_date)
        queryParams.append("from_date", convertLocalToUTC(params.from_date));
      if (params?.to_date)
        queryParams.append("to_date", convertLocalToUTC(params.to_date));
    });

    const url = `${BASE_URL}toteManagement/getToteList${queryString}`;
    const response = await axiosInstance.get(url);
    return response.data;
  },

  getTotePriorityList: async (): Promise<TotePriorityOption[]> => {
    const response = await axiosInstance.get<TotePriorityListResponse>(
      `${BASE_URL}toteManagement/listPriorityStatus`
    );
    const data = response.data;

    if (Array.isArray(data?.tote_priority)) {
      return data.tote_priority.map((item) => ({
        id: item.id,
        priority: item.priority,
      }));
    }

    return [];
  },

  markTotePriority: async (
    toteRecordId: number,
    priorityId: number
  ): Promise<MarkTotePriorityResponse> => {
    const response = await axiosInstance.put(
      `${BASE_URL}toteManagement/markTotePriority`,
      {
        id: toteRecordId,
        priority_id: priorityId,
      }
    );
    return response.data;
  },

  markTrolleyServiceable: async (
    trolleyId: string | number,
    isServiceable: boolean
  ): Promise<MarkTrolleyServiceableResponse> => {
    const response = await axiosInstance.put(
      `${BASE_URL}trolleyManagement/markTrolleyServiceable`,
      {
        trolley_id: trolleyId,
        is_serviceable: isServiceable,
      }
    );
    return response.data;
  },

  getNotifications: async (
    filterType?: "all" | "unread"
  ): Promise<GetNotificationsResponse> => {
    const queryString = buildQueryString((queryParams) => {
      if (filterType) {
        queryParams.append("filter_type", filterType);
      }
    });

    const response = await axiosInstance.get<GetNotificationsResponse>(
      `${BASE_URL}notificationManagement/getNotifications${queryString}`
    );
    return response.data;
  },

  markNotifications: async (
    payload: MarkNotificationsRequest
  ): Promise<MarkNotificationsResponse> => {
    const response = await axiosInstance.put<MarkNotificationsResponse>(
      `${BASE_URL}notificationManagement/markNotifications`,
      payload
    );
    return response.data;
  },
};
